import { SubmissionParticipantForReception } from "@dune/types";
import {
	Checkbox,
	H3,
	Table,
	TableBody,
	TableCell,
	TableHeader,
	TableRow,
} from "@dune/ui";
import {
	formatCurrency,
	getParticipantCompanyName,
	traductActivities,
} from "@dune/utils";
import { useInsurerModal } from "../../hooks/useInsurerModal";
import { useShouldDisplayHTAmount } from "../../hooks/useParticipantToSubmissionForm";
import { InsurerSelectionModal } from "../../pages/constructionSites/components/modals/InsurerSelectionModal";
import { ParticipantDocumentsReception } from "../../pages/constructionSites/components/intervenants/ParticipantDocumentsReception";
import {
	BaseSubmissionParticipantTableProps,
	ParticipantInsurerTableCell,
	ParticipantsTableCell,
	ParticipantsTableHead,
} from "./participantsTable.helper";

interface BuildersTableReceptionProps
	extends BaseSubmissionParticipantTableProps<SubmissionParticipantForReception> {
	onDocumentClick?: (file: {
		id: string;
		key: string;
		bucket: string;
		mimeType: string;
		name: string;
	}) => void;
	loadingDocumentId?: string | null;
}

export const BuildersTableReception = ({
	submissionParticipants,
	constructionSiteId,
	selectedParticipantId,
	onParticipantSelect,
	onDocumentClick,
	loadingDocumentId,
	displayInsurer = false,
	disabledDocuments,
	disabledInputs,
}: BuildersTableReceptionProps) => {
	const { shouldDisplayHT } = useShouldDisplayHTAmount(constructionSiteId);
	const { modalState, setModalState, closeModal } = useInsurerModal();

	const totalAmount = submissionParticipants.reduce(
		(acc, submissionParticipant) => {
			const amount =
				shouldDisplayHT && submissionParticipant.totalFeesHT != null
					? submissionParticipant.totalFeesHT
					: submissionParticipant.totalFeesTTC;
			return acc + amount;
		},
		0,
	);

	return (
		<div className="mb-4">
			<H3>RÉALISATEURS</H3>
			<div className="bg-white rounded-lg border overflow-x-hidden">
				<Table className="table-fixed w-full overflow-x-auto">
					<TableHeader>
						<TableRow>
							<ParticipantsTableHead className="w-64">
								Raison sociale
							</ParticipantsTableHead>
							<ParticipantsTableHead className="w-48">
								Activités réalisées
							</ParticipantsTableHead>
							{displayInsurer && (
								<ParticipantsTableHead className="w-60">
									Assureur
								</ParticipantsTableHead>
							)}
							<ParticipantsTableHead className="w-48">
								Montant du marché {shouldDisplayHT ? "(HT)" : "(TTC)"}
							</ParticipantsTableHead>
							<ParticipantsTableHead>
								Pièces justificatives
							</ParticipantsTableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{submissionParticipants.length > 0 ? (
							submissionParticipants.map((submissionParticipant) => {
								const activitiesRaw = traductActivities(
									submissionParticipant.totalMissionsOrActivitiesOrMaterialTypes,
								);

								const amount =
									shouldDisplayHT && submissionParticipant.totalFeesHT != null
										? submissionParticipant.totalFeesHT
										: submissionParticipant.totalFeesTTC;
								const companyName = getParticipantCompanyName(
									submissionParticipant.participant,
								);

								const isSelected =
									selectedParticipantId ===
									submissionParticipant.participant.id;

								return (
									<TableRow key={submissionParticipant.id}>
										<ParticipantsTableCell>
											<div className="flex items-center gap-4">
												{!disabledInputs && (
													<Checkbox
														id={`builder-${submissionParticipant.id}`}
														checked={isSelected}
														onCheckedChange={(checked) => {
															onParticipantSelect(
																submissionParticipant.participant.id,
																checked === true,
															);
														}}
													/>
												)}
												<label
													htmlFor={`builder-${submissionParticipant.id}`}
													className="truncate"
												>
													{companyName}
												</label>
											</div>
										</ParticipantsTableCell>
										<ParticipantsTableCell
											tooltipContent={
												<div className="flex flex-col gap-1">
													{activitiesRaw.length > 0 ? (
														activitiesRaw.map((activity) => (
															<span key={activity}>{activity}</span>
														))
													) : (
														<span>-</span>
													)}
												</div>
											}
										>
											{activitiesRaw.length > 0
												? activitiesRaw.join(", ")
												: "-"}
										</ParticipantsTableCell>
										{displayInsurer && (
											<ParticipantInsurerTableCell
												setModalState={setModalState}
												participantInsurer={submissionParticipant.insurer}
												participantId={submissionParticipant.participant.id}
												constructionSiteId={constructionSiteId}
											/>
										)}
										<ParticipantsTableCell>
											{formatCurrency(amount)}
										</ParticipantsTableCell>
										<ParticipantsTableCell innerClassName="overflow-hidden min-w-0">
											<ParticipantDocumentsReception
												submissionParticipant={submissionParticipant}
												constructionSiteId={constructionSiteId}
												onDocumentClick={onDocumentClick}
												loadingDocumentId={loadingDocumentId}
												disabledDocuments={disabledDocuments}
											/>
										</ParticipantsTableCell>
									</TableRow>
								);
							})
						) : (
							<TableRow>
								<TableCell
									colSpan={displayInsurer ? 6 : 5}
									className="text-center text-gray-500 py-4"
								>
									Aucun réalisateur
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
				<div className="flex justify-end px-6 py-4 font-bold text-gray-700 border-t">
					Total réalisateurs
					<span className="ml-4 w-40 text-right">
						{formatCurrency(totalAmount)}
					</span>
				</div>
			</div>
			<InsurerSelectionModal
				isOpen={modalState.isOpen}
				onClose={closeModal}
				currentInsurer={modalState.currentInsurer}
				participantId={modalState.participantId}
				constructionSiteId={modalState.constructionSiteId}
				onSuccess={closeModal}
			/>
		</div>
	);
};
